import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import SelectSearchInput from './../SelectSearch/index';
import AgeingDebtors from './AgeingDebtors';
import CustomerCreditSection from "./CustomerCreditSection";
import Footer from "../Footer";
import CreditLeftSection from "./CreditLeftSection";
import { selectStyles } from "../../utils/cssUtils";
import PaymentModal from './../Modal/paymentModal';

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.error || error?.response?.data?.message || fallback;

const CUSTOMER_DETAIL_HEADER = ["ID", "Name", "Email", "Address", "Phone", "Pan No."];

const INITIAL_LOADING = {
    debtors: false,
    customers: false,
    similarCustomers: false,
    creditDetails: false,
    creditLeft: false,
};

const INITIAL_ERRORS = {
    debtors: null,
    customers: null,
    similarCustomers: null,
    creditDetails: null,
    creditLeft: null,
};

const formatDateForApi = (date) => date.toLocaleDateString("en-CA");

const Credit = () => {
    const url = process.env.REACT_APP_BASE_URL;
    const navigate = useNavigate();

    const formattedDate = new Date()
        .toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        .replace(/(\w+) (\d+) (\w+) (\d+)/, "$1, $2 $3, $4");

    const [token, setToken] = useState("");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState("");

    const [listCustomer, setListCustomer] = useState([]);
    const [similarCustomer, setSimilarCustomer] = useState([]);

    const [creditDetails, setCreditDetails] = useState({});
    const [creditWiseBillList, setCreditWiseBillList] = useState([]);
    const [creditWisePaymentList, setCreditWisePaymentList] = useState([]);

    const [debtData, setDebtData] = useState([]);
    const [creditLeft, setCreditLeft] = useState([]);

    // Single source of truth for loading/error state, keyed per request.
    const [loading, setLoading] = useState(INITIAL_LOADING);
    const [errors, setErrors] = useState(INITIAL_ERRORS);
    const setLoadingKey = (key, value) => setLoading((prev) => ({ ...prev, [key]: value }));
    const setErrorKey = (key, value) => setErrors((prev) => ({ ...prev, [key]: value }));

    const [showCustomerDetails, setShowCustomerDetails] = useState(false);
    const [showBills, setShowBills] = useState(false);
    const [showPayments, setShowPayments] = useState(false);
    const [showCreditLeft, setShowCreditLeft] = useState(false);
    const [uniqueGuestID, setUniqueGuestID] = useState("");

    const [rangeType, setRangeType] = useState("");
    const [showDatepicker, setShowDatepicker] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const [paymentModalOpen, setPaymentModalOpen] = useState(false);

    // Auth guard
    useEffect(() => {
        const tokenCheck = localStorage.getItem("token");
        const typeCheck = localStorage.getItem("type");

        if (!tokenCheck && typeCheck === "kitchen") {
            navigate("/");
        } else {
            setToken(tokenCheck);
        }
    }, [navigate]);

    // Reset everything when the outlet changes
    useEffect(() => {
        setSimilarCustomer([]);
        setSelectedCustomer("");
        setCreditDetails({});
        setCreditWiseBillList([]);
        setCreditWisePaymentList([]);
        setCreditLeft([]);
        setShowCreditLeft(false);
        setShowCustomerDetails(false);
        setShowDatepicker(false);
        setRangeType("");
        setStartDate(new Date());
        setEndDate(new Date());
    }, [selectedOutlet]);

    // Clear stale details when the customer changes
    useEffect(() => {
        setCreditWiseBillList([]);
        setCreditDetails({});
    }, [selectedCustomer]);

    // Fetch the customer list for the selected outlet
    useEffect(() => {
        if (!selectedOutlet || !token) return;

        const fetchCustomerList = async () => {
            setLoadingKey("customers", true);
            setErrorKey("customers", null);

            try {
                const response = await axios.post(`${url}/customerCredit`, {
                    outlet: selectedOutlet,
                    token,
                });
                setListCustomer(response.data);
            } catch (error) {
                console.error(error);
                setErrorKey("customers", getErrorMessage(error, "Failed to load customers"));
                setListCustomer([]);
            } finally {
                setLoadingKey("customers", false);
            }
        };

        fetchCustomerList();
    }, [token, selectedOutlet, url]);

    // Fetch customers matching the selected name
    const fetchSelectedData = useCallback(async () => {
        if (!selectedOutlet || !selectedCustomer) return;

        setLoadingKey("similarCustomers", true);
        setErrorKey("similarCustomers", null);

        try {
            const response = await axios.post(`${url}/customerCreditData`, {
                token,
                outlet: selectedOutlet,
                customerName: selectedCustomer,
            });
            setSimilarCustomer(response.data);
        } catch (error) {
            console.error(error);
            setErrorKey("similarCustomers", getErrorMessage(error, "Failed to load customer data"));
            setSimilarCustomer([]);
        } finally {
            setLoadingKey("similarCustomers", false);
        }
    }, [url, token, selectedOutlet, selectedCustomer]);

    useEffect(() => {
        fetchSelectedData();
    }, [fetchSelectedData]);

    // Fetch credit details for a specific guest
    const fetchCreditDetails = useCallback(
        async (guestID) => {
            setLoadingKey("creditDetails", true);
            setErrorKey("creditDetails", null);

            try {
                const response = await axios.post(`${url}/customerCreditDetails`, {
                    token,
                    outlet: selectedOutlet,
                    CustomerName: selectedCustomer,
                    guestID,
                });
                setCreditDetails(response.data.CreditDetails);
                setCreditWiseBillList(response.data.CreditWiseBillList);
                setCreditWisePaymentList(response.data.CreditWisePaymentList);
            } catch (error) {
                console.error(error);
                setErrorKey("creditDetails", getErrorMessage(error, "Failed to load credit details"));
            } finally {
                setLoadingKey("creditDetails", false);
            }
        },
        [url, token, selectedOutlet, selectedCustomer]
    );

    // Auto-open details when there's exactly one matching customer
    useEffect(() => {
        if (similarCustomer.length === 1) {
            const oneID = similarCustomer[0].guestID;
            setShowCustomerDetails(true);
            setUniqueGuestID(oneID);
            fetchCreditDetails(oneID);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [similarCustomer]);

    // Fetch debtors ageing summary
    useEffect(() => {
        if (!token || !selectedOutlet) return;

        const fetchDebtData = async () => {
            setLoadingKey("debtors", true);
            setErrorKey("debtors", null);

            try {
                const response = await axios.post(`${url}/debtors-summary`, {
                    token,
                    outlet: selectedOutlet,
                });
                setDebtData(response.data);
            } catch (error) {
                console.error(error);
                setErrorKey("debtors", getErrorMessage(error, "Error fetching debtors data"));
            } finally {
                setLoadingKey("debtors", false);
            }
        };

        fetchDebtData();
    }, [selectedOutlet, token, url]);

    useEffect(() => {
        if (!rangeType) return;

        const fetchCreditLeft = async () => {
            setLoadingKey("creditLeft", true);
            setErrorKey("creditLeft", null);

            const payload = { token, outlet: selectedOutlet, type: rangeType };

            if (rangeType === "Ranged") {
                setShowDatepicker(true);
                if (!startDate || !endDate) {
                    setLoadingKey("creditLeft", false);
                    return;
                }

                payload.dateStart = formatDateForApi(startDate);
                payload.dateEnd = formatDateForApi(endDate);
            } else {
                setShowDatepicker(false);
            }

            try {
                const response = await axios.post(`${url}/customerCreditleft`, payload);
                setCreditLeft(response.data);
            } catch (error) {
                console.error(error);
                setErrorKey("creditLeft", getErrorMessage(error, "Failed to load credit data"));
            } finally {
                setLoadingKey("creditLeft", false);
            }
        };

        fetchCreditLeft();
    }, [rangeType, startDate, endDate, token, selectedOutlet, url]);

    const handleDetails = (id) => {
        setShowCustomerDetails(true);
        setUniqueGuestID(id);
        fetchCreditDetails(id);
    };

    const handleRangeType = (event) => {
        const value = event.target.value;
        setRangeType(value);
        setShowDatepicker(value === "Ranged");
    };

    const handleStartDateChange = (value) => {
        setStartDate(value);
    };

    const handleEndDateChange = (value) => {
        setEndDate(value);
    };

    return (
        <section className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />

            <div className="flex-1 w-full p-3 max-w-6xl mx-auto">
                <div className="flex justify-center mb-2.5">
                    <SelectSearchInput
                        token={token}
                        setSelectedOutlet={setSelectedOutlet}
                        selectedOutlet={selectedOutlet}
                        compact
                    />
                </div>

                <div className="text-center font-semibold text-lg text-gray-700 mb-2.5">
                    Summary of Debtors ageing as of {formattedDate}
                </div>

                <AgeingDebtors debtData={debtData} loading={loading.debtors} error={errors.debtors} />

                <div className="flex justify-end mt-4">
                    <button
                        onClick={() => setShowCreditLeft((prev) => !prev)}
                        className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-sm transition"
                    >
                        {showCreditLeft ? "Hide Credit Remaining" : "View Credit Remaining"}
                    </button>
                </div>

                {showCreditLeft && (
                    <CreditLeftSection
                        handleRangeType={handleRangeType}
                        creditLeft={creditLeft}
                        showDatepicker={showDatepicker}
                        startDate={startDate}
                        endDate={endDate}
                        handleStartDateChange={handleStartDateChange}
                        handleEndDateChange={handleEndDateChange}
                        selectedCustomer={selectedCustomer}
                        rangeType={rangeType}
                        rangeLoading={loading.creditLeft}
                        rangeError={errors.creditLeft}
                        formatDateForApi={formatDateForApi}
                    />
                )}

                <div className="mt-4">
                    <div className="flex justify-between items-center mb-2.5">
                        <div className="flex-1"></div>

                        <div className="flex-1 text-center text-base text-gray-700 font-semibold">
                            Debtor Details
                        </div>

                        <div className="flex-1 flex justify-end">
                            <div className="w-[300px] z-30">
                                <Select
                                    options={listCustomer.map((c) => ({ value: c.value, label: c.name }))}
                                    value={
                                        selectedCustomer
                                            ? { value: selectedCustomer, label: selectedCustomer }
                                            : null
                                    }
                                    onChange={(selectedOption) =>
                                        setSelectedCustomer(selectedOption ? selectedOption.value : "")
                                    }
                                    placeholder="Select Customer Name"
                                    isDisabled={!selectedOutlet || loading.customers}
                                    isLoading={loading.customers}
                                    isClearable
                                    isSearchable
                                    styles={{
                                        ...selectStyles,
                                        menuPortal: (base) => ({
                                            ...(selectStyles.menuPortal ? selectStyles.menuPortal(base) : base),
                                            zIndex: 9999,
                                        }),
                                    }}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                />
                                {errors.customers && (
                                    <p className="text-red-600 text-sm mt-1">{errors.customers}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <CustomerCreditSection
                        header={CUSTOMER_DETAIL_HEADER}
                        similarCustomer={similarCustomer}
                        similarCustomerLoading={loading.similarCustomers}
                        similarCustomerError={errors.similarCustomers}
                        handleDetails={handleDetails}
                        token={token}
                        selectedOutlet={selectedOutlet}
                        url={url}
                        fetchSelectedData={fetchSelectedData}
                        showCustomerDetails={showCustomerDetails}
                        creditDetails={creditDetails}
                        creditWiseBillList={creditWiseBillList}
                        creditWisePaymentList={creditWisePaymentList}
                        detailsLoading={loading.creditDetails}
                        detailsError={errors.creditDetails}
                        showBills={showBills}
                        toggleBills={() => setShowBills((prev) => !prev)}
                        showPayments={showPayments}
                        togglePayments={() => setShowPayments((prev) => !prev)}
                        selectedCustomer={selectedCustomer}
                        onMakePayment={() => setPaymentModalOpen(true)}
                    />
                </div>

                <PaymentModal
                    show={paymentModalOpen}
                    handleClose={() => setPaymentModalOpen(false)}
                    token={token}
                    selectedOutlet={selectedOutlet}
                    selectedCustomer={selectedCustomer}
                    uniqueID={uniqueGuestID}
                    setCreditLeft={setCreditLeft}
                    setCreditDetails={setCreditDetails}
                    setCreditWiseBillList={setCreditWiseBillList}
                    setCreditWisePaymentList={setCreditWisePaymentList}
                />
            </div>

            <Footer />
        </section>
    );
};

export default Credit;